// Математика масштабирования фото в просмотрщике (pinch / двойной тап / колесо / pan). Чистые функции —
// без DOM: рамку (размеры сцены и раскладочного бокса картинки) измеряет вызывающий.
//
// Модель: картинка лежит в своём раскладочном боксе (offsetLeft/Top/Width/Height внутри сцены, трансформ
// на них не влияет) и рисуется с `transform-origin: 0 0; transform: translate(x, y) scale(s)`. Точка
// картинки u (в её собственных, немасштабированных px) видна в координатах бокса в `x + s·u`. Все точки
// жестов здесь — в координатах бокса (начало = левый верхний угол раскладочного бокса).
export const ZOOM_IDENTITY = Object.freeze({ scale: 1, x: 0, y: 0 });
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 5;
/** Кратность двойного тапа/двойного клика. */
export const ZOOM_DOUBLE_TAP = 2.5;
/** Всё, что ближе к 1 — считаем исходным видом (без «полупиксельного» зума после щипка назад). */
const ZOOM_EPS = 0.01;
export function clampNumber(value, min, max) {
    return Math.min(max, Math.max(min, value));
}
export function distance(a, b) {
    return Math.hypot(b.x - a.x, b.y - a.y);
}
export function midpoint(a, b) {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
/** Увеличено ли фото (свайп/колесо листают и клик по фону закрывает только в исходном виде). */
export function isZoomed(t) {
    return t.scale > 1 + ZOOM_EPS;
}
/** Значение для data-атрибута/отладки: «2.50». */
export function formatZoom(scale) {
    return scale.toFixed(2);
}
/** Клиентская точка (clientX/Y) → координаты бокса картинки. */
export function toBoxPoint(client, frame) {
    return {
        x: client.x - frame.stageLeft - frame.boxLeft,
        y: client.y - frame.stageTop - frame.boxTop,
    };
}
/**
 * Ограничение сдвига по одной оси: увеличенная картинка шире сцены — край не отходит от края сцены;
 * уже сцены — центрируется по этой оси.
 */
function clampAxis(offset, scale, boxStart, boxSize, stageSize) {
    const size = scale * boxSize;
    if (size <= stageSize)
        return (stageSize - size) / 2 - boxStart;
    return clampNumber(offset, stageSize - boxStart - size, -boxStart);
}
/** Приводит преобразование к допустимому: масштаб в [MIN, MAX], сдвиг — в границах сцены. */
export function clampPan(t, frame) {
    const scale = clampNumber(t.scale, ZOOM_MIN, ZOOM_MAX);
    if (scale <= 1 + ZOOM_EPS)
        return ZOOM_IDENTITY;
    return {
        scale,
        x: clampAxis(t.x, scale, frame.boxLeft, frame.boxW, frame.stageW),
        y: clampAxis(t.y, scale, frame.boxTop, frame.boxH, frame.stageH),
    };
}
/**
 * Масштаб `nextScale` с якорем: точка картинки под `anchor` (координаты бокса) остаётся под ним
 * (затем — ограничение границами, у краёв якорь может сместиться, чтобы не открыть пустоту).
 */
export function zoomAt(t, nextScale, anchor, frame) {
    const scale = clampNumber(nextScale, ZOOM_MIN, ZOOM_MAX);
    if (scale <= 1 + ZOOM_EPS)
        return ZOOM_IDENTITY;
    const k = scale / t.scale;
    return clampPan({
        scale,
        x: anchor.x - (anchor.x - t.x) * k,
        y: anchor.y - (anchor.y - t.y) * k,
    }, frame);
}
/** Двойной тап/клик: увеличено → исходный вид; иначе — ZOOM_DOUBLE_TAP в точке тапа. */
export function toggleZoomAt(t, anchor, frame) {
    if (isZoomed(t))
        return ZOOM_IDENTITY;
    return zoomAt(t, ZOOM_DOUBLE_TAP, anchor, frame);
}
/** Сдвиг (pan) от исходного преобразования жеста на (dx, dy) — с ограничением границами. */
export function panBy(start, dx, dy, frame) {
    if (!isZoomed(start))
        return ZOOM_IDENTITY;
    return clampPan({ scale: start.scale, x: start.x + dx, y: start.y + dy }, frame);
}
/**
 * Щипок: от состояния начала жеста (start + две исходные точки) к текущим двум точкам. Масштаб —
 * отношение расстояний между пальцами; точка картинки под исходной серединой щипка едет за текущей
 * серединой (зум и сдвиг двумя пальцами одновременно, как в галерее телефона). Точки — в координатах бокса.
 */
export function pinchTransform(start, startA, startB, curA, curB, frame) {
    const d0 = distance(startA, startB);
    if (d0 < 1)
        return start; // пальцы в одной точке — щипка нет (защита от деления на ~0)
    const scale = clampNumber(start.scale * (distance(curA, curB) / d0), ZOOM_MIN, ZOOM_MAX);
    if (scale <= 1 + ZOOM_EPS)
        return ZOOM_IDENTITY;
    const m0 = midpoint(startA, startB);
    const m1 = midpoint(curA, curB);
    // точка картинки (немасштабированная) под исходной серединой
    const ux = (m0.x - start.x) / start.scale;
    const uy = (m0.y - start.y) / start.scale;
    return clampPan({ scale, x: m1.x - ux * scale, y: m1.y - uy * scale }, frame);
}
/** Дельта колеса в px (deltaMode: 0 — px, 1 — строки, 2 — страницы); одиночный «рывок» ограничен. */
export function wheelDeltaPx(delta, deltaMode) {
    const px = deltaMode === 1 ? delta * 16 : deltaMode === 2 ? delta * 400 : delta;
    return clampNumber(px, -200, 200);
}
/**
 * Множитель масштаба для колеса с Ctrl (так приходят щипки тачпада и Ctrl+колесо мыши): вверх
 * (deltaY<0) — увеличение. Щелчок колеса (100 px) ≈ ×1.28; мелкие дельты тачпада — плавно.
 */
export function wheelZoomFactor(deltaY, deltaMode) {
    return Math.exp(-wheelDeltaPx(deltaY, deltaMode) / 400);
}
//# sourceMappingURL=zoom.js.map